import { useState } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';

import { useStoreActions } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import { extractErrorMessage } from '~/utils/error';
import { createValues } from './form';
import Button from '~/ui/components/common/Button';
import NoDataFound from '~/ui/components/reusable/NoDataFound';
import GoalModal from '../popups/GoalModal';
import Item from './Item';

import { ICarePlanGoal } from '~/services/api/carePlanGoals/types';
import { IFormValues } from '../popups/GoalModal/form/types';
import { IPagePermissions } from '../../types';
import styles from './Goals.module.scss';

interface IProps {
  patientId: number;
  items: ICarePlanGoal[];
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Goals = ({ patientId, items, permissions, onCarePlanChange }: IProps) => {
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { onCreateCarePlanGoal } = useStoreActions(actions => actions.carePlanGoals);

  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleCreateGoal = async (values: IFormValues) => {
    const payload = {
      ...values,
      date: isoToFormat(values.date, 'y-MM-dd'),
    } as ICarePlanGoal;

    try {
      setIsCreating(true);
      await onCreateCarePlanGoal({ patientId, data: payload });
      onCarePlanChange();
      setShowCreate(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div className={styles.title}>Goals</div>
        {permissions.canManage && (
          <Button variant="contained" color="primary" onClick={() => setShowCreate(true)}>
            + Add New
          </Button>
        )}
      </div>
      <div className={styles.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Date</TableCell>
                <TableCell>Reason</TableCell>
                {permissions.canManage && <TableCell width={120} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length ? (
                items.map(item => (
                  <Item
                    key={item.id}
                    item={item}
                    permissions={permissions}
                    onCarePlanChange={onCarePlanChange}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {showCreate && (
        <GoalModal
          initialValues={createValues}
          isProcessing={isCreating}
          onSubmit={handleCreateGoal}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
};

export default Goals;
